/**
 * Custom element for a countdown timer to a specific date and time.
 * The element will hide itself when outside the start/end date range,
 * except when in the theme editor.
 */
customElements.define(
  'campaign-bar',
  class CampaignBar extends HTMLElement {
    connectedCallback() {
      this.isThemeEditor = window.Shopify?.designMode;

      const dateAttrs = ['start', 'end'].reduce(
        (acc, prefix) => ({
          ...acc,
          [`${prefix}Year`]: parseInt(this.getAttribute(`data-${prefix}-year`)),
          [`${prefix}Month`]: parseInt(this.getAttribute(`data-${prefix}-month`)) - 1,
          [`${prefix}Day`]: parseInt(this.getAttribute(`data-${prefix}-day`)),
          [`${prefix}Hour`]: parseInt(this.getAttribute(`data-${prefix}-hour`)),
        }),
        {}
      );

      if (Object.values(dateAttrs).some(isNaN)) {
        console.error('Invalid date attributes provided');
        return;
      }

      Object.assign(this, dateAttrs);

      this.startDate = new Date(this.startYear, this.startMonth, this.startDay, this.startHour);
      this.endDate = new Date(this.endYear, this.endMonth, this.endDay, this.endHour);

      const now = new Date();
      if (!this.isThemeEditor && now < this.startDate) {
        this.style.display = 'none';
        return;
      }

      this.timerContainer = this.querySelector('#timer');
      this.timerElements = ['days', 'hours', 'minutes', 'seconds'].reduce(
        (acc, unit) => ({
          ...acc,
          [unit]: this.querySelector(`[data-${unit}]`),
        }),
        {}
      );

      this.startCountdown();
    }

    /**
     * Starts the countdown timer and updates the timer elements every second.
     * Hides the element when before start date range, except in theme editor.
     * Hides timer and shows minus values after end date.
     */
    startCountdown() {
      this.updateTimer();
      this.interval = setInterval(() => this.updateTimer(), 1000);
    }

    updateTimer() {
      const now = new Date();

      if (!this.isThemeEditor && now < this.startDate) {
        clearInterval(this.interval);
        this.style.display = 'none';
        return;
      }

      // Handle post-end date behavior
      const isPastEndDate = now > this.endDate;
      if (isPastEndDate && !this.isThemeEditor) {
        if (this.timerContainer) {
          this.timerContainer.style.display = 'none';
        }
        return;
      }

      let timeLeft;
      if (isPastEndDate) {
        // Calculate negative time (time since end date)
        timeLeft = now - this.endDate;
      } else {
        // Calculate positive time (time until end date)
        timeLeft = this.endDate - now;
      }

      const times = {
        days: Math.floor(timeLeft / (1000 * 60 * 60 * 24)),
        hours: Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((timeLeft % (1000 * 60)) / 1000),
      };

      Object.entries(times).forEach(([unit, value]) => {
        if (this.timerElements[unit]) {
          // Show negative sign for past end date
          let displayValue = String(value).padStart(2, '0');
          if (isPastEndDate) {
            displayValue = `-${displayValue}`;
          }
          this.timerElements[unit].textContent = displayValue;
        }
      });
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }
  }
);
